"use client";

import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Logo } from "@/components/common/logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PortalBrand } from "./portal-brand";
import {
  FlowSegmentedProgress,
  type FlowJourneyProgress,
} from "./flow-progress";

type FlowWizardHeaderProps = {
  title: string;
  headingLevel?: "h1" | "h2";
  titleBadge?: ReactNode;
  onClose?: () => void;
  closeLabel: string;
  stepIndicator?: ReactNode;
  headerAction?: ReactNode;
  brand?: {
    name: string;
    avatarUrl?: string | null;
    licenseNumber?: string | null;
    subtitle?: string | null;
  };
  showLogo: boolean;
  branded: boolean;
  inlineProgress: boolean;
  titleSize: "default" | "compact";
  chrome: "default" | "lead";
  journeyProgress?: FlowJourneyProgress | null;
};

export function FlowWizardHeader({
  title,
  headingLevel = "h1",
  titleBadge,
  onClose,
  closeLabel,
  stepIndicator,
  headerAction,
  brand,
  showLogo,
  branded,
  inlineProgress,
  titleSize,
  chrome,
  journeyProgress,
}: FlowWizardHeaderProps) {
  const Title = headingLevel;
  const compactHeader = branded || inlineProgress;

  return (
    <header
      className={cn(
        "relative shrink-0",
        compactHeader
          ? chrome === "lead"
            ? "min-h-14"
            : "h-[3.75rem]"
          : chrome === "lead"
            ? "min-h-14"
            : "h-16 border-b",
      )}
    >
      <div
        className={cn(
          "grid items-center px-4 sm:px-6",
          chrome === "lead" ? "min-h-14 py-1.5" : "h-full",
          compactHeader || !stepIndicator
            ? "grid-cols-[minmax(0,1fr)_auto]"
            : "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
        )}
      >
        <div className="flex w-full min-w-0 items-center gap-2.5 justify-self-start">
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label={closeLabel}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
          {brand ? (
            <>
              <PortalBrand {...brand} />
              <Title className="sr-only">{title}</Title>
              {titleBadge}
            </>
          ) : branded || showLogo ? (
            <>
              <Logo small size="xs" className="shrink-0" />
              <Title
                className={cn(
                  branded ? "sr-only" : "truncate font-medium",
                  titleSize === "compact"
                    ? "text-sm text-muted-foreground"
                    : "text-base sm:text-2xl",
                )}
              >
                {title}
              </Title>
              {titleBadge}
            </>
          ) : (
            <>
              <Title
                className={cn(
                  "truncate font-medium",
                  inlineProgress || titleSize === "compact"
                    ? "text-sm text-muted-foreground"
                    : "text-base sm:text-2xl",
                )}
              >
                {title}
              </Title>
              {titleBadge}
            </>
          )}
        </div>

        {!compactHeader && stepIndicator ? (
          <div className="justify-self-center px-2">{stepIndicator}</div>
        ) : null}

        <div className="flex shrink-0 items-center gap-3 pl-3 justify-self-end">
          {branded && journeyProgress ? (
            <span className="text-sm font-medium tabular-nums text-muted-foreground">
              {journeyProgress.percentage}%
            </span>
          ) : null}
          {inlineProgress && journeyProgress ? (
            <span className="text-sm font-medium tabular-nums text-muted-foreground">
              {journeyProgress.current} of {journeyProgress.total}
            </span>
          ) : null}
          {headerAction}
        </div>
      </div>

      {compactHeader && journeyProgress ? (
        <div className="absolute inset-x-0 bottom-0 px-4 sm:px-6">
          <FlowSegmentedProgress
            segments={journeyProgress.segments}
            percentage={journeyProgress.percentage}
            className="h-[3px]"
          />
        </div>
      ) : null}
    </header>
  );
}
